import * as STD                                                 from '@emersya/mpa-std-lib';
import CM                                                       from '@emersya/mpa-components-manager-lib';

export interface IMenuPanelComponentConfiguration extends IMenuViewsComponentConfiguration {
	defaultSection? : CM.Component.TId;
};

export class MenuPanelComponent extends MenuViewsComponent<
	MenuPanelComponent,
	IMenuPanelComponentConfiguration
>
{
	static override initialize() {
		MenuViewsComponent.initialize.call(this);
		this.declareEventType('menu-open');
		this.declareEventType('menu-close');
	};

	override async component_initialize() {
		await super.component_initialize();
		this.addEventListener('value', this.onInputButton.bind(this), {
			subType :  'input-button',
			listener : this
		});
	};

	protected override async component_onVisibilityUpdateBeforeChilds(isVisible : boolean) {
		if (isVisible && !this.currentSection) {
			await this.openDefaultMaybe();
		}
	};

	async openMaybe(childId? : string) {
		if (childId) {
			return (await this.open(childId));
		}
	};

	override async open(childId : string) {
		if (this.currentSection) {
			if (this.currentSection.id === childId) {
				return;
			}
			await this.close();
		}
		await super.open(childId);
		const child = <
			SectionComponent | undefined
			> this.component_findOuter([ childId ]);
		if (!child) {
			this.component_throwUserError(`Child<${ childId }> not found!`, { childId });
		}
		this.currentSection = child;
		child.component_DOM.classList.add('opened');
		await this.component_dispatchVisibilityUpdateOnChilds();
		await this.currentSection.component_dispatchVisibilityUpdateOnChilds();
		const childs = this.component_outerChilds.filter(
			x => x instanceof CM.DOMComponent
		) as CM.DOMComponent<any, any, any>[];
		for (const c of childs) {
			await c.component_dispatchVisibilityUpdateOnChilds();
		}
		await this.dispatchEvent('menu-open', {
			details : {
				section : this.currentSection
			}
		});
	};

	async openDefaultMaybe() {
		if (this.component_configuration.defaultSection) {
			const defaultChild = this.component_find(`@.${ this.component_configuration.defaultSection }`);
			if (!defaultChild.component_isDisabled) {
				await this.open(this.component_configuration.defaultSection);
			} else {
				const firstEnabledChild = this.component_outerChilds.find(x => !x.component_isDisabled);
				if (firstEnabledChild) {
					await this.open(firstEnabledChild.id);
				}
			}
		}
	};

	override async close() {
		if (!this.currentSection) {
			return;
		}
		const oldSection = this.currentSection;
		this.currentSection = null;
		oldSection.component_DOM.classList.remove('opened');
		await super.close();
		await this.component_dispatchVisibilityUpdateOnChilds();
		await oldSection.component_dispatchVisibilityUpdateOnChilds();
		const childs = this.component_innerChilds.filter(
			x => x instanceof CM.DOMComponent
		) as CM.DOMComponent<any, any, any>[];
		for (const c of childs) {
			await c.component_dispatchVisibilityUpdateOnChilds();
		}
		await this.dispatchEvent('menu-close', {
			details : {
				section : this.currentSection
			}
		});
	};

	private async onInputButton(ev : STD.events.Event<any, CM.Component<any, any>>) {
		if (!(ev.target instanceof CM.Component)) {
			throw STD.errors.user('');
		}
		const k : CM.Component.TAny | null = ev.target;
		if (!k) {
			throw STD.errors.user('');
		}
		if ((!k.component_parent) || (k.component_parent.component_parent !== this) || (k.id !== 'sectionHeader')) {
			return;
		}
		const section = k.component_parent;
		await this.open(section.id);
	};

	protected override component_childIsVisible(
		child : CM.DOMComponent<any, any, any>
	)
	{
		if (!this.currentSection) {
			return (child.component_isActive());
		}
		return (
			(this.currentSection === child) &&
			child.component_isActive()
		);
	};

	override component_childIsActive(child : CM.Component<any, any>) {
		if (this.currentSection) {
			if (this.currentSection !== child) {
				return (false);
			}
		}
		return (super.component_childIsActive(child));
	};

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component>[0],
		configuration : IMenuPanelComponentConfiguration
	)
	{
		super(descriptor, configuration);
		this.component_DOM = <HTMLDivElement> STD.DOM.factory({});
	};
};
export namespace NS {
	export const Component = MenuPanelComponent;
	export type Component = MenuPanelComponent;
	export type Configuration = IMenuPanelComponentConfiguration;
}
;
import './MenuPanel.scss';
import { MenuViewsComponent, IMenuViewsComponentConfiguration } from '../MenuViews/MenuViews';
import { SectionComponent }                                     from '../../Section/Section';
import manifestXMLSource                                        from './MenuPanel.xml';

CM.declare({
	id :       'menu-panel',
	type :     MenuPanelComponent,
	manifest : manifestXMLSource
});
